import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../../axiosBaseQuery";
import { mutateWithCsrf } from "../../../../api/csrfMutation";
import {
  ORGANIZATION_ENDPOINTS,
  SITE_ENDPOINTS,
  INSTALLATION_ENDPOINTS,
  ORG_DOMAIN_ENDPOINTS,
  ORG_MEMBERSHIP_ENDPOINTS,
} from "../../../../api/constants";

/** Гранты требуют BFF CSRF; смена auth-состояния или abort запрещают POST/результат. */
async function requestGrant(url, data, api) {
  const user = api.getState().auth?.user;
  const current = () => Boolean(user) && !api.signal.aborted && api.getState().auth?.user === user;
  try {
    const receipt = await mutateWithCsrf({ url, data, status: 201, current, csrfUrl: "installation-reservations/csrf/" });
    if (!receipt || typeof receipt.grant !== "string" || !receipt.grant.trim() ||
        typeof receipt.id !== "string" || !receipt.id) throw new Error("invalid-grant-receipt");
    return { data: receipt };
  } catch (error) {
    // Не помещаем сырой ответ (потенциально с grant) в Redux/error toast.
    return { error: { status: error.response?.status || "CUSTOM_ERROR", data: { code: "GRANT_REQUEST_FAILED" } } };
  }
}

/**
 * RTK Query API организаций EyeHub.
 *
 * Покрывает каталог: организации, площадки, инсталляции, домены, членство.
 * Ответы-списки приходят уже развёрнутыми из envelope ({data} → тело), в форме
 * UnifiedOffsetPagination: {results, count, pageIndex, pageCount, hasNext, ...}.
 *
 * ВАЖНО (ORG-1). Бэкенд фильтрует вложенные списки по `org`, а НЕ по
 * `organization` (`SiteViewSet.filterset_fields=["org","city"]`,
 * `OrgDomainViewSet=["org","kind","status"]`, `OrgMembershipViewSet=["org",…]`).
 * Неизвестный query-параметр django-filter молча игнорирует, поэтому старый
 * `?organization=` возвращал ВЕСЬ каталог: hub-admin, открыв организацию A,
 * видел и мог отозвать домен организации B. Аргумент `organization` оставлен как
 * deprecated-алиас (его ещё передают Enrollment/Licenses/Maintenance) и
 * нормализуется в `org` — литерал `organization` в params не уходит никогда.
 *
 * Инсталляции фильтруются по `site` (точная площадка) либо по `site__org`
 * (все инсталляции организации). Фильтр `site__org` добавляется бэкендом
 * параллельно; пока его нет, ответ придёт нефильтрованным — страницы обязаны
 * дополнительно сопоставлять `installation.site` со своим списком площадок.
 *
 * Endpoints:
 *   - organizations/?status=&ordering=&search=  (список + деталь + удаление)
 *   - sites/?org=                (площадки организации; PATCH/DELETE детали)
 *   - installations/?site=|?site__org=          (инсталляции; PATCH/DELETE)
 *   - org-domains/?org=&kind=    (домены; verify/revoke — actions, DELETE детали)
 *   - org-memberships/?org=      (членство; deactivate/activate — actions)
 */
export const organizationsApi = createApi({
  reducerPath: "organizationsApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Organization", "OrgDomain", "OrgMembership", "Site", "Installation"],
  endpoints: (builder) => ({
    // === Организации ===
    getOrganizations: builder.query({
      query: ({ limit = 20, offset = 0, search, status, ordering } = {}) => ({
        url: ORGANIZATION_ENDPOINTS.LIST,
        method: "GET",
        params: {
          limit,
          offset,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          ...(ordering ? { ordering } : {}),
        },
      }),
      providesTags: [{ type: "Organization", id: "LIST" }],
    }),

    getOrganization: builder.query({
      query: (id) => ({ url: ORGANIZATION_ENDPOINTS.DETAIL(id), method: "GET" }),
      providesTags: (result, error, id) => [{ type: "Organization", id }],
    }),

    // Создание организации (hub-admin). slug задаётся при создании и неизменяем.
    createOrganization: builder.mutation({
      query: (data) => ({ url: ORGANIZATION_ENDPOINTS.CREATE, method: "POST", data }),
      invalidatesTags: [{ type: "Organization", id: "LIST" }],
    }),

    // Обновление организации (имя / переход статуса). slug НЕ отправляем (immutable).
    updateOrganization: builder.mutation({
      query: ({ id, body }) => ({
        url: ORGANIZATION_ENDPOINTS.UPDATE(id),
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Organization", id },
        { type: "Organization", id: "LIST" },
      ],
    }),

    // Удаление организации (hub-admin). Каскад: площадки → инсталляции → домены →
    // членства. Вызывается только из «опасной зоны» с подтверждением по slug.
    deleteOrganization: builder.mutation({
      query: (id) => ({ url: ORGANIZATION_ENDPOINTS.DETAIL(id), method: "DELETE" }),
      invalidatesTags: [
        { type: "Organization", id: "LIST" },
        { type: "Site", id: "LIST" },
        { type: "Installation", id: "LIST" },
        { type: "OrgDomain", id: "LIST" },
        { type: "OrgMembership", id: "LIST" },
      ],
    }),

    // === Площадки / инсталляции ===
    getSites: builder.query({
      // `organization` — deprecated-алиас (ORG-1), нормализуется в `org`.
      query: ({ org, organization, search, limit = 100, offset = 0 } = {}) => {
        const orgId = org ?? organization;
        return {
          url: SITE_ENDPOINTS.LIST,
          method: "GET",
          params: {
            limit,
            offset,
            ...(orgId ? { org: orgId } : {}),
            ...(search ? { search } : {}),
          },
        };
      },
      providesTags: [{ type: "Site", id: "LIST" }],
    }),

    getSite: builder.query({
      query: (id) => ({ url: SITE_ENDPOINTS.DETAIL(id), method: "GET" }),
      providesTags: (result, error, id) => [{ type: "Site", id }],
    }),

    // Создание площадки. relay_enabled=true отклоняется 400 без verified+CAA
    // relay-домена — ошибку показываем в toast на уровне страницы.
    createSite: builder.mutation({
      query: (data) => ({ url: SITE_ENDPOINTS.CREATE, method: "POST", data }),
      invalidatesTags: [{ type: "Site", id: "LIST" }],
    }),

    // Правка площадки: name/city/timezone + флаги маршрутизации relay_enabled /
    // prefer_internal (ORG-4). Именно здесь включается relay — при создании
    // площадки гейт `can_enable_relay` всегда даёт 400 (инстанса ещё нет).
    updateSite: builder.mutation({
      query: ({ id, body }) => ({ url: SITE_ENDPOINTS.DETAIL(id), method: "PATCH", data: body }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Site", id },
        { type: "Site", id: "LIST" },
        { type: "Installation", id: "LIST" },
      ],
    }),

    deleteSite: builder.mutation({
      query: (id) => ({ url: SITE_ENDPOINTS.DETAIL(id), method: "DELETE" }),
      invalidatesTags: [
        { type: "Site", id: "LIST" },
        { type: "Installation", id: "LIST" },
        { type: "OrgDomain", id: "LIST" },
        { type: "OrgMembership", id: "LIST" },
      ],
    }),

    getInstallations: builder.query({
      // `site` — точная площадка; `site__org` — все инсталляции организации.
      // `organization` — deprecated-алиас, нормализуется в `site__org`.
      query: ({ site, site__org: siteOrg, organization, limit = 100, offset = 0 } = {}) => {
        const orgId = siteOrg ?? organization;
        return {
          url: INSTALLATION_ENDPOINTS.LIST,
          method: "GET",
          params: {
            limit,
            offset,
            ...(site ? { site } : {}),
            ...(!site && orgId ? { site__org: orgId } : {}),
          },
        };
      },
      providesTags: [{ type: "Installation", id: "LIST" }],
    }),

    // Создание инсталляции для площадки (host_label + internal_url).
    createInstallation: builder.mutation({
      query: (data) => ({ url: INSTALLATION_ENDPOINTS.CREATE, method: "POST", data }),
      invalidatesTags: [{ type: "Installation", id: "LIST" }],
    }),

    // Правка инсталляции (host_label / internal_url / status). `site` иммутабелен —
    // сериализатор его отбрасывает, поэтому в body не отправляем.
    updateInstallation: builder.mutation({
      query: ({ id, body }) => ({
        url: INSTALLATION_ENDPOINTS.DETAIL(id),
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: [
        { type: "Installation", id: "LIST" },
        { type: "Site", id: "LIST" },
      ],
    }),

    createSshGrant: builder.mutation({
      queryFn: ({ installationId, pubkey, ttl = 3600 }, api) =>
        requestGrant(INSTALLATION_ENDPOINTS.SSH_GRANT(installationId), { pubkey, ttl }, api),
    }),

    createIsoGrant: builder.mutation({
      queryFn: ({ installationId, iso_sha256 }, api) =>
        requestGrant(INSTALLATION_ENDPOINTS.ISO_GRANT(installationId), { iso_sha256 }, api),
    }),

    deleteInstallation: builder.mutation({
      query: (id) => ({ url: INSTALLATION_ENDPOINTS.DETAIL(id), method: "DELETE" }),
      invalidatesTags: [
        { type: "Installation", id: "LIST" },
        { type: "Site", id: "LIST" },
      ],
    }),

    // === Домены ===
    getOrgDomains: builder.query({
      // `organization` — deprecated-алиас (ORG-1), нормализуется в `org`.
      query: ({ org, organization, kind, status, search, limit = 100, offset = 0 } = {}) => {
        const orgId = org ?? organization;
        return {
          url: ORG_DOMAIN_ENDPOINTS.LIST,
          method: "GET",
          params: {
            limit,
            offset,
            ...(orgId ? { org: orgId } : {}),
            ...(kind ? { kind } : {}),
            ...(status ? { status } : {}),
            ...(search ? { search } : {}),
          },
        };
      },
      providesTags: [{ type: "OrgDomain", id: "LIST" }],
    }),

    // Регистрация домена (kind: email|service|relay; site — опционально).
    createOrgDomain: builder.mutation({
      query: (data) => ({ url: ORG_DOMAIN_ENDPOINTS.CREATE, method: "POST", data }),
      invalidatesTags: [{ type: "OrgDomain", id: "LIST" }],
    }),

    verifyOrgDomain: builder.mutation({
      query: (id) => ({ url: ORG_DOMAIN_ENDPOINTS.VERIFY(id), method: "POST" }),
      invalidatesTags: [
        { type: "OrgDomain", id: "LIST" },
        { type: "Site", id: "LIST" },
      ],
    }),

    // Отзыв домена — POST org-domains/{id}/revoke/ (мягкий: запись остаётся).
    revokeOrgDomain: builder.mutation({
      query: (id) => ({
        url: ORG_DOMAIN_ENDPOINTS.REVOKE(id),
        method: "POST",
      }),
      invalidatesTags: [
        { type: "OrgDomain", id: "LIST" },
        { type: "Site", id: "LIST" },
      ],
    }),

    // Правка домена: сейчас нужна ровно для ACME-аккаунта relay-домена. Без него хаб
    // подставляет в требуемую CAA-запись заглушку `<ACME-аккаунт клиники>`, и клиника
    // физически не может опубликовать правильное значение — то есть relay не поднять
    // без обращения к нам. Имя домена после подтверждения бэкенд менять запрещает.
    updateOrgDomain: builder.mutation({
      query: ({ id, ...data }) => ({ url: ORG_DOMAIN_ENDPOINTS.DETAIL(id), method: "PATCH", data }),
      invalidatesTags: [{ type: "OrgDomain", id: "LIST" }],
    }),

    // Жёсткое удаление домена (ORG-3): отозванная запись держит
    // unique_together (org, domain, kind) и мешает зарегистрировать домен заново.
    deleteOrgDomain: builder.mutation({
      query: (id) => ({ url: ORG_DOMAIN_ENDPOINTS.DETAIL(id), method: "DELETE" }),
      invalidatesTags: [{ type: "OrgDomain", id: "LIST" }],
    }),

    // === Членство ===
    getOrgMemberships: builder.query({
      // `organization` — deprecated-алиас (ORG-1), нормализуется в `org`.
      query: ({ org, organization, role, status, search, limit = 100, offset = 0 } = {}) => {
        const orgId = org ?? organization;
        return {
          url: ORG_MEMBERSHIP_ENDPOINTS.LIST,
          method: "GET",
          params: {
            limit,
            offset,
            ...(orgId ? { org: orgId } : {}),
            ...(role ? { role } : {}),
            ...(status ? { status } : {}),
            ...(search ? { search } : {}),
          },
        };
      },
      providesTags: [{ type: "OrgMembership", id: "LIST" }],
    }),

    // Приглашение сотрудника (org + keycloak_id + email + role, опц. home_site).
    createOrgMembership: builder.mutation({
      query: (data) => ({ url: ORG_MEMBERSHIP_ENDPOINTS.CREATE, method: "POST", data }),
      invalidatesTags: [{ type: "OrgMembership", id: "LIST" }],
    }),

    // Правка участника — PATCH org-memberships/{id}/ {email, role, home_site}.
    updateOrgMembership: builder.mutation({
      query: ({ id, body }) => ({
        url: ORG_MEMBERSHIP_ENDPOINTS.DETAIL(id),
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: [{ type: "OrgMembership", id: "LIST" }],
    }),

    deactivateOrgMembership: builder.mutation({
      query: (id) => ({
        url: ORG_MEMBERSHIP_ENDPOINTS.DEACTIVATE(id),
        method: "POST",
      }),
      invalidatesTags: [{ type: "OrgMembership", id: "LIST" }],
    }),

    // Возврат ошибочно деактивированного сотрудника (ORG-6). Путь собирается из
    // DETAIL(id), пока ORG_MEMBERSHIP_ENDPOINTS.ACTIVATE не заведён в constants.js
    // (файл общий и правится не здесь) — литерала пути в страницах при этом нет.
    activateOrgMembership: builder.mutation({
      query: (id) => ({
        url: `${ORG_MEMBERSHIP_ENDPOINTS.DETAIL(id)}activate/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "OrgMembership", id: "LIST" }],
    }),
  }),
});

export const {
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetSitesQuery,
  useGetSiteQuery,
  useCreateSiteMutation,
  useUpdateSiteMutation,
  useDeleteSiteMutation,
  useGetInstallationsQuery,
  useCreateInstallationMutation,
  useUpdateInstallationMutation,
  useDeleteInstallationMutation,
  useCreateSshGrantMutation,
  useCreateIsoGrantMutation,
  useGetOrgDomainsQuery,
  useCreateOrgDomainMutation,
  useVerifyOrgDomainMutation,
  useRevokeOrgDomainMutation,
  useUpdateOrgDomainMutation,
  useDeleteOrgDomainMutation,
  useGetOrgMembershipsQuery,
  useCreateOrgMembershipMutation,
  useUpdateOrgMembershipMutation,
  useDeactivateOrgMembershipMutation,
  useActivateOrgMembershipMutation,
} = organizationsApi;

export default organizationsApi;
